import Model, { attr, belongsTo } from '@warp-drive/legacy/model';
import type Recommendation from './recommendation';
import type WellnessTrainModel from './wellness-train';

const matchesApp =
  (requestingApp: 'STRATEGY_FINDER' | 'PSLF' | 'IDR') =>
  (recommendation: Recommendation): boolean =>
    recommendation.requestingApp === requestingApp;

const byCreatedAt = (a: Recommendation, b: Recommendation): number =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

// remains to be seen how important these are
export type UserProfileWellnessTrainComponentType =
  | 'score'
  | 'add_account'
  | 'doesnt_have_loans'
  | 'not_adding_loans'
  | 'dash_summary'
  | 'to_coaching'
  | 'to_pslf_tool'
  | 'to_idr_tool'
  | 'to_nslds_upload'
  | 'to_refinancing_tool'
  | 'to_strategy_finder'
  | 'to_pslf_form_submission'
  | 'transitional_strategy_finder_results'
  | 'transitional_pslf_form_submission'
  | 'transitional_pslf_tool_results'
  | 'transitional_idr_results';

// remains to be seen how important *these* are, too
export type UserProfileWellnessTrainComponentName =
  | 'score_on_track'
  | 'score_need_adjustment'
  | 'score_need_attention'
  | 'prompt_to_add_account'
  | 'summary_pslf'
  | 'summary_pslf_review'
  | 'summary_payment_interest'
  | 'summary_congrats'
  | 'summary_being_behind'
  | 'summary_private_loans'
  | 'summary_pslf_no_payment_due'
  | 'summary_good_standing_pslf'
  | 'summary_grace_period'
  | 'summary_congrats_past_payments'
  | 'summary_challenging'
  | 'summary_smart_idr'
  | 'summary_consolidate_ffel'
  | 'summary_parent_plus'
  | 'summary_pslf_grace_consolidate'
  | 'summary_congrats_pslf'
  | 'summary_heard_pslf'
  | 'summary_pslf_renew_idr'
  | 'summary_pslf_no_credit'
  | 'summary_pslf_ffel'
  | 'summary_pslf_direct_ffel'
  | 'summary_pslf_direct_perkins'
  | 'recommend_coaching_consolidate_parent_plus'
  | 'recommend_pslf_tool'
  | 'recommend_coaching'
  | 'recommend_idr_tool_now'
  | 'recommend_nslds_upload'
  | 'recommend_coaching_your_benefit'
  | 'recommend_refinancing_tool'
  | 'recommend_coaching_short'
  | 'recommend_coaching_pslf'
  | 'recommend_strategy_finder_now'
  | 'recommend_pslf_form_submission'
  | 'recommend_coaching_extra_payment'
  | 'recommend_pslf_tool_before_payment'
  | 'recommend_pslf_tool_during_payment'
  | 'recommend_pslf_form_submission_also'
  | 'recommend_idr_tool'
  | 'recommend_strategy_finder'
  | 'recommend_refinancing_tool_maybe'
  | 'recommend_coaching_affordability'
  | 'recommend_coaching_consolidation'
  | 'recommend_coaching_parent_plus'
  | 'recommend_coaching_consolidation_direct'
  | 'recommend_strategy_finder_short'
  | 'recommend_coaching_affordability_pslf'
  | 'recommend_strategy_finder_pslf'
  | 'recommend_pslf_form_submission_unique'
  | 'recommend_coaching_consolidation_ffel'
  | 'recommend_coaching_consolidation_pslf'
  | 'recommend_coaching_consolidation_perkins'
  | 'transition_strategy_finder'
  | 'transition_pslf_form'
  | 'transition_pslf_tool'
  | 'transition_idr_tool';

export default class WellnessInsightsComponentDefinition extends Model {
  @attr declare type: UserProfileWellnessTrainComponentType;
  @attr declare name: UserProfileWellnessTrainComponentName;
  @attr declare subtitle: string;
  @attr declare text: string;

  // the API provides these in an ordered list and the order matters; see serializer
  // for how this attr gets set
  @attr declare order: number;

  @belongsTo('wellness-train', { async: false, inverse: null })
  declare wellnessTrain: WellnessTrainModel;

  getCurrentRecommendation(requestingApp: 'STRATEGY_FINDER' | 'PSLF' | 'IDR'): Recommendation {
    const recommendation = this.wellnessTrain.user.recommendations
      .filter(matchesApp(requestingApp))
      .sort(byCreatedAt)
      .at(0);

    if (!recommendation) {
      throw new Error(
        `Missing ${requestingApp} recommendation for WellnessInsightsComponentDefinition ${this.id}`
      );
    }

    return recommendation;
  }
}
